import * as yup from "yup";
import { request } from "@/common/api";
import { useForm } from "react-hook-form";
import { showAlert } from "@/components/containers/Alert";
import { LoginForm } from "@/components/organisms/Form/LoginForm";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { saveFcmToken } from "@/common/functions/flutter";
import { BaseResponse } from "@/common/types/baseRespones";
import { LoginControl } from "@/components/containers/Controls/LoginControl";
import { RadioControl } from "@/components/containers/Controls/RadioControl";
import { joinCommState } from "@/common/states/recoilInfo";
import { LoginTemplate } from "@/components/templates/LoginTemplate";
import { InputCheckbox } from "@/components/atoms/Inputs/InputCheckbox";
import { userIdInputState } from "@/common/states/login";
import { useEffect, useState } from "react";
import { agreeState, typeState } from "@/common/states/agree";
import { GuardianUser, StudentUser } from "@/common/types/user";
import { useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import {
  writeAccessToken,
  writeRefreshToken,
} from "@/common/functions/authFunctions";
import {
  guardianUserState,
  loginTypeState,
  studentUserState,
} from "@/common/states/user";

const schema = yup.object({
  userId: yup.string().nullable().required("아이디를 입력해 주세요. "),
  password: yup.string().nullable().required("비밀번호를 입력해 주세요."),
  userType: yup.string().nullable().required("사용자 유형을 선택해 주세요."),
});

export interface LoginInputs {
  userType: string;
  userId: string;
  password: string;
}

interface LoginData {
  accessToken?: string;
  refreshToken?: string;
  logId: number;
  user: StudentUser | GuardianUser;
  userType: string;
}

interface LoginResponse extends BaseResponse<LoginData> {}

export const LoginPage = () => {
  /** 🛣️ Navigation Capability Acquisition */
  const navigate = useNavigate();

  /** 📦 State Store */
  const setStudentUser = useSetRecoilState(studentUserState);
  const setGuardianUser = useSetRecoilState(guardianUserState);
  const resetTypeState = useResetRecoilState(typeState);
  const resetAgreeState = useResetRecoilState(agreeState);
  const resetJoinCommState = useResetRecoilState(joinCommState);
  const resetLoginTypeState = useResetRecoilState(loginTypeState);
  const resetStudentUserState = useResetRecoilState(studentUserState);
  const resetGuardianUserState = useResetRecoilState(guardianUserState);
  const [loginType, setLoginType] = useRecoilState(loginTypeState);
  const [savedUserId, setSavedUserId] = useRecoilState(userIdInputState);
  const [checked, setChecked] = useState(savedUserId !== null);

  /** 📝 Form State Definition */
  const { control, handleSubmit, setValue } = useForm<LoginInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      userType: loginType || "",
    },
  });

  /** 📡 API Layer */
  // 학생 로그인
  const { mutateAsync: studentlogin } = useMutation({
    mutationFn: (inputs: LoginInputs) => {
      return request<LoginResponse>({
        method: "POST",
        url: "/auth/student/login",
        data: inputs,
      });
    },
    onSuccess: async (res: LoginResponse, inputs: LoginInputs) =>
      onLoginSuceess(res, inputs),
  });

  // 법정 보호자 로그인
  const { mutateAsync: guardianlogin } = useMutation({
    mutationFn: (inputs: LoginInputs) => {
      return request<LoginResponse>({
        method: "POST",
        url: "/auth/guardian/login",
        data: inputs,
      });
    },
    onSuccess: async (res: LoginResponse, inputs: LoginInputs) =>
      onLoginSuceess(res, inputs),
  });

  /** 🔄 Side Effect Management */
  const onLoginSuceess = (res: LoginResponse, inputs: LoginInputs) => {
    const { data, success, message } = res;

    if (!success) {
      return showAlert(message ?? "로그인에 실패하였습니다.");
    }

    data?.accessToken && writeAccessToken(data.accessToken);
    data?.refreshToken && writeRefreshToken(data.refreshToken);

    if (data?.user?.userType === "student") {
      data?.user && setStudentUser(data.user as StudentUser);
    } else {
      data?.user && setGuardianUser(data.user as GuardianUser);
    }

    if (checked) {
      setSavedUserId(inputs.userId ?? null);
      localStorage.setItem("loginType", inputs.userType);
    } else {
      setSavedUserId(null);
      localStorage.removeItem("loginType");
    }

    // Flutter에 FCM 토큰 저장 요청 메시지 전송
    if (window.WebViewChannel) {
      saveFcmToken(data?.user?.id, data?.user?.userType);
    }

    navigate("/", { replace: true });
  };

  /** 🗺️ Event Handlers */
  const onSubmit = async (inputs: LoginInputs) => {
    try {
      if (inputs.userType === "student") {
        await studentlogin(inputs);
      }
      if (inputs.userType === "guardian") await guardianlogin(inputs);
      setLoginType(inputs.userType as "student" | "guardian");
    } catch (e: any) {
      showAlert("서버요청에러");
    }
  };

  /** 🔄 Side Effect Management */
  useEffect(() => {
    resetTypeState();
    resetAgreeState();
    resetJoinCommState();
    resetLoginTypeState();
    resetStudentUserState();
    resetGuardianUserState();
  }, [
    resetTypeState,
    resetAgreeState,
    resetJoinCommState,
    resetLoginTypeState,
    resetStudentUserState,
    resetGuardianUserState,
  ]);

  useEffect(() => {
    const savedLoginType = localStorage.getItem("loginType");
    if (savedLoginType) {
      setLoginType(savedLoginType as "student" | "guardian");
      setValue("userType", savedLoginType);
    }
  }, [setLoginType, setValue]);

  return (
    <>
      <LoginTemplate>
        <>
          <LoginForm
            onSubmit={handleSubmit(onSubmit)}
            typeSelect={
              <RadioControl
                width="100%"
                size="sm"
                control={control}
                name="userType"
                data={[
                  {
                    value: "student",
                    displayExpr: "학생",
                  },
                  { value: "guardian", displayExpr: "법정보호자" },
                ]}
              />
            }
          >
            <>
              <LoginControl
                type="text"
                control={control}
                name="userId"
                label="아이디"
                placeholder="아이디를 입력해 주세요."
                tabIndex={1}
                defaultValue={savedUserId ?? ""}
              />
              <LoginControl
                type="password"
                control={control}
                name="password"
                label="비밀번호"
                placeholder="비밀번호를 입력해 주세요."
                tabIndex={2}
              />
              <InputCheckbox
                name="save_id"
                data={[{ id: 1, value: true, displayExpr: "아이디 저장" }]}
                onChange={(e) => {
                  setChecked(e.target.checked);
                  if (e.target.checked) {
                    localStorage.setItem(
                      "loginType",
                      loginType as "student" | " guardian"
                    );
                  } else {
                    localStorage.removeItem("loginType");
                  }
                }}
                value={checked}
              />
            </>
          </LoginForm>
        </>
      </LoginTemplate>
    </>
  );
};
